import React, { memo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Avatar,
  useTheme,
  alpha,
  CircularProgress,
  IconButton,
  Collapse,
} from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';

/**
 * Message bubble component for chat messages
 */
const MessageBubble = memo(
  ({ isUser, text, isLoading, translations, storeId }) => {
    const theme = useTheme();
    const isMobile = window.matchMedia('(max-width: 600px)').matches;

    // State to manage code block expansion/collapse
    // Use a key that includes store ID to maintain separate state per store
    const [expandedCodeBlocks, setExpandedCodeBlocks] = useState({});

    // Define markdown components for rendering
    const MarkdownComponents = {
      // Code block rendering with syntax highlighting
      code({ inline, className, children, ...props }) {
        const match = /language-(\w+)/.exec(className || '');

        // Only apply collapsible functionality to non-inline code blocks
        if (!inline && match) {
          // Generate a unique ID for this code block based on content
          const blockId = btoa(String(children).substring(0, 50)).replace(
            /[^a-zA-Z0-9]/g,
            ''
          );
          // Create a unique key that includes the store ID if available
          const storeKey = storeId ? `${storeId}_${blockId}` : blockId;
          const isExpanded = expandedCodeBlocks[storeKey] !== false; // Default to expanded

          const toggleExpand = (e) => {
            e.stopPropagation();
            setExpandedCodeBlocks((prev) => ({
              ...prev,
              [storeKey]: !isExpanded,
            }));
          };

          return (
            <Box
              sx={{
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                borderRadius: '6px',
                my: 1,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 1.5,
                  py: 0.5,
                  borderBottom: isExpanded
                    ? `1px solid ${alpha(theme.palette.divider, 0.1)}`
                    : 'none',
                  backgroundColor: alpha(theme.palette.background.default, 0.4),
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ fontFamily: 'monospace', color: 'text.secondary' }}
                >
                  {match[1]}
                </Typography>
                <IconButton
                  size="small"
                  onClick={toggleExpand}
                  sx={{ p: 0.5 }}
                  aria-label={
                    isExpanded
                      ? translations.ai_chat_code_block_collapse
                      : translations.ai_chat_code_block_expand
                  }
                  title={
                    isExpanded
                      ? translations.ai_chat_code_block_collapse
                      : translations.ai_chat_code_block_expand
                  }
                >
                  {isExpanded ? (
                    <ExpandLessIcon fontSize="small" />
                  ) : (
                    <ExpandMoreIcon fontSize="small" />
                  )}
                </IconButton>
              </Box>
              <Collapse in={isExpanded}>
                <SyntaxHighlighter
                  style={tomorrow}
                  language={match[1]}
                  PreTag="div"
                  {...props}
                  customStyle={{ margin: 0, borderRadius: 0 }}
                >
                  {String(children).replace(/\n$/, '')}
                </SyntaxHighlighter>
              </Collapse>
            </Box>
          );
        }

        return (
          <code
            className={className}
            {...props}
            sx={{
              backgroundColor: alpha(theme.palette.background.default, 0.7),
              borderRadius: '4px',
              padding: '2px 4px',
              fontFamily: 'monospace',
              fontSize: '0.85em',
            }}
          >
            {children}
          </code>
        );
      },
      // Override paragraph elements
      p: (props) => (
        <Typography variant="body2" {...props} sx={{ mt: 0.5, mb: 0.5 }} />
      ),
      // Override link elements
      a: (props) => (
        <a
          style={{
            color: theme.palette.primary.main,
            textDecoration: 'underline',
          }}
          target="_blank"
          rel="noopener noreferrer"
          {...props}
        />
      ),
    };

    // Calculate message styling based on sender
    const getMessageBackground = () => {
      if (isUser) {
        return theme.palette.mode === 'dark'
          ? alpha(theme.palette.primary.dark, 0.7)
          : alpha(theme.palette.primary.light, 0.15);
      } else {
        return theme.palette.mode === 'dark'
          ? alpha(theme.palette.background.paper, 0.9)
          : '#ffffff';
      }
    };

    const getMessageColor = () => {
      return isUser
        ? theme.palette.mode === 'dark'
          ? '#ffffff'
          : theme.palette.primary.dark
        : theme.palette.text.primary;
    };

    const getBorderColor = () => {
      if (isUser) {
        return theme.palette.mode === 'dark'
          ? alpha(theme.palette.primary.dark, 0.5)
          : alpha(theme.palette.primary.main, 0.3);
      } else {
        return theme.palette.mode === 'dark'
          ? alpha(theme.palette.divider, 0.2)
          : '#e0e0e0';
      }
    };

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          mb: { xs: 2, sm: 3 },
          alignItems: 'flex-start',
          justifyContent: isUser ? 'flex-end' : 'flex-start',
          pl: isUser ? { xs: 3, sm: 6 } : 0,
          pr: isUser ? 0 : { xs: 3, sm: 6 },
        }}
      >
        {/* AI Avatar */}
        {!isUser && (
          <Avatar
            sx={{
              bgcolor: (theme) =>
                theme.palette.mode === 'dark'
                  ? alpha(theme.palette.secondary.main, 0.8)
                  : '#3f7ad3',
              mr: { xs: 1.5, sm: 2 },
              width: { xs: 32, sm: 38 },
              height: { xs: 32, sm: 38 },
              color: 'white',
              boxShadow: (theme) =>
                theme.palette.mode === 'dark'
                  ? 'none'
                  : '0 1px 2px rgba(0,0,0,0.1)',
            }}
          >
            <SmartToyIcon fontSize={isMobile ? 'small' : 'medium'} />
          </Avatar>
        )}

        <Box sx={{ maxWidth: { xs: '90%', sm: '85%' } }}>
          {/* AI Label */}
          {!isUser && (
            <Typography
              variant="caption"
              gutterBottom
              sx={{
                mb: 0.5,
                display: 'block',
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                ml: { xs: 0.5, sm: 0 },
                color: (theme) =>
                  theme.palette.mode === 'dark' ? 'text.secondary' : '#3f7ad3',
                fontWeight: 500,
              }}
            >
              {translations.ai_chat_title}
            </Typography>
          )}

          {/* Loading indicator */}
          {isLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', ml: 1, py: 1 }}>
              <CircularProgress size={20} thickness={5} />
              <Typography variant="body2" sx={{ ml: 1.5 }}>
                {translations.ai_chat_loading}
              </Typography>
            </Box>
          ) : (
            <Paper
              elevation={0}
              sx={{
                p: { xs: 1.5, sm: 2 },
                backgroundColor: getMessageBackground,
                color: getMessageColor,
                borderRadius: '12px',
                borderTopRightRadius: isUser ? '4px' : '12px',
                borderTopLeftRadius: isUser ? '12px' : '4px',
                wordBreak: 'break-word',
                border: '1px solid',
                borderColor: getBorderColor,
                boxShadow: isUser ? '0 1px 2px rgba(0,0,0,0.07)' : 'none',
              }}
            >
              {isUser ? (
                <Typography
                  variant="body2"
                  sx={{
                    color: getMessageColor,
                    fontSize: { xs: '0.875rem', sm: '0.875rem' },
                    lineHeight: { xs: 1.4, sm: 1.5 },
                  }}
                >
                  {text}
                </Typography>
              ) : (
                <Box
                  sx={{
                    color: getMessageColor,
                    fontSize: { xs: '0.875rem', sm: '0.875rem' },
                    lineHeight: { xs: 1.4, sm: 1.5 },
                    '& code': {
                      backgroundColor: (theme) =>
                        alpha(theme.palette.background.default, 0.7),
                      borderRadius: '4px',
                      padding: '2px 4px',
                      fontFamily: 'monospace',
                      fontSize: '0.85em',
                    },
                    '& pre': {
                      backgroundColor: 'transparent',
                      border: (theme) =>
                        `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      borderRadius: '6px',
                      margin: '8px 0',
                      padding: 0,
                      overflow: 'hidden',
                    },
                    '& pre > div': {
                      borderRadius: 0,
                      margin: 0,
                    },
                    '& p': {
                      marginTop: '0.5em',
                      marginBottom: '0.5em',
                    },
                    '& h1, & h2, & h3, & h4, & h5, & h6': {
                      marginTop: '0.75em',
                      marginBottom: '0.25em',
                      fontWeight: 'bold',
                    },
                    '& ul, & ol': {
                      paddingLeft: '1.5em',
                    },
                    '& blockquote': {
                      borderLeft: (theme) =>
                        `3px solid ${alpha(theme.palette.primary.main, 0.6)}`,
                      paddingLeft: '1em',
                      margin: '0.5em 0',
                      color: 'text.secondary',
                    },
                    '& a': {
                      color: 'primary.main',
                    },
                    '& table': {
                      borderCollapse: 'collapse',
                      width: '100%',
                      marginBottom: '1em',
                    },
                    '& th, & td': {
                      border: (theme) =>
                        `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                      padding: '4px 8px',
                      textAlign: 'left',
                    },
                    '& th': {
                      backgroundColor: (theme) =>
                        alpha(theme.palette.background.default, 0.6),
                    },
                    '& hr': {
                      border: 0,
                      height: '1px',
                      backgroundColor: 'divider',
                      margin: '1em 0',
                    },
                    '& strong': {
                      fontWeight: 'bold',
                    },
                    '& em': {
                      fontStyle: 'italic',
                    },
                  }}
                >
                  <ReactMarkdown components={MarkdownComponents}>
                    {text}
                  </ReactMarkdown>
                </Box>
              )}
            </Paper>
          )}
        </Box>

        {/* User Avatar */}
        {isUser && (
          <Avatar
            sx={{
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.8),
              ml: { xs: 1.5, sm: 2 },
              width: { xs: 32, sm: 38 },
              height: { xs: 32, sm: 38 },
              color: 'white',
            }}
          >
            <PersonIcon fontSize={isMobile ? 'small' : 'medium'} />
          </Avatar>
        )}
      </Box>
    );
  }
);

MessageBubble.displayName = 'MessageBubble';

export default MessageBubble;
